// ── Supabase client — single initialisation point ─────────────────────────
// Replaces utils/firebase.js
// Install: npm install @supabase/supabase-js

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn('[Supabase] Missing env vars — running in mock mode');
}

export const supabase = SUPABASE_URL
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken:    true,
        persistSession:      true,
        detectSessionInUrl:  true,
      },
      realtime: {
        params: { eventsPerSecond: 10 },
      },
    })
  : null;

// ── Table helpers (type-safe shortcuts) ──────────────────────────────────
export const db = {
  // ── Normalized user tables (FNBC) ──────────────────────────────────────
  user_core:          () => supabase?.from('user_core'),
  user_profiles:      () => supabase?.from('user_profiles'),
  user_scores:        () => supabase?.from('user_scores'),
  // ── Backward-compat view (still works for SELECT *) ───────────────────
  users:              () => supabase?.from('users'),
  // ── Relationships ──────────────────────────────────────────────────────
  likes:              () => supabase?.from('likes'),
  passes:             () => supabase?.from('passes'),
  matches:            () => supabase?.from('matches'),
  conversations:      () => supabase?.from('conversations'),
  messages:           () => supabase?.from('messages'),
  friend_ratings:     () => supabase?.from('friend_ratings'),
  friend_rating_axes: () => supabase?.from('friend_rating_axes'),
  // ── Events ─────────────────────────────────────────────────────────────
  events:             () => supabase?.from('events'),
  events_full:        () => supabase?.from('events_full'),   // view with prices
  event_pricing:      () => supabase?.from('event_pricing'),
  event_registrations:() => supabase?.from('event_registrations'),
  // ── Scouts ─────────────────────────────────────────────────────────────
  scouts:             () => supabase?.from('scouts'),
  scout_members:      () => supabase?.from('scout_members'),
  scout_waitlist:     () => supabase?.from('scout_waitlist'),
  network_features:   () => supabase?.from('network_features'),
  // ── CML & Revenue ──────────────────────────────────────────────────────
  cml_managers:       () => supabase?.from('cml_managers'),
  cml_revenue:        () => supabase?.from('cml_revenue'),
  cml_revenue_full:   () => supabase?.from('cml_revenue_full'), // view with computed shares
  revenue_rules:      () => supabase?.from('revenue_rules'),
  sponsors:           () => supabase?.from('sponsors'),
  // ── Other ──────────────────────────────────────────────────────────────
  notifications:      () => supabase?.from('notifications'),
  user_daily_usage:   () => supabase?.from('user_daily_usage'),
  user_boosts:        () => supabase?.from('user_boosts'),
  payments:           () => supabase?.from('payments'),
  referrals:          () => supabase?.from('referrals'),
  blocks:             () => supabase?.from('blocks'),
  reports:            () => supabase?.from('reports'),
  verifications:      () => supabase?.from('verifications'),
  countries:          () => supabase?.from('countries'),
  country_plan_pricing: () => supabase?.from('country_plan_pricing'),
  networks:           () => supabase?.from('networks'),
  active_networks:    () => supabase?.from('active_networks'),    // view: country.active AND network.active
  translations:       () => supabase?.from('translations'),
  app_features:       () => supabase?.from('app_features'),
  plan_features:      () => supabase?.from('plan_features'),
  plan_pricing:       () => supabase?.from('plan_pricing'),
  invitations:        () => supabase?.from('invitations'),
  // ── Feature Pack A ──────────────────────────────────────────────────────
  friend_invites_sent:    () => supabase?.from('friend_invites_sent'),
  date_checkins:          () => supabase?.from('date_checkins'),
  coffee_roulette:        () => supabase?.from('coffee_roulette_signups'),
  scout_sponsors:         () => supabase?.from('scout_sponsors'),
};

// ── Targeted update helpers (write to correct sub-table) ─────────────────
// Use these instead of db.users().update() for targeted writes
export const dbUpdate = {
  // Core identity fields (email, name, role, plan, mood…)
  userCore:    (uid, fields) => supabase?.from('user_core').update(fields).eq('id', uid),
  // Profile fields (bio, job, photos, interests, lifestyle…)
  userProfile: (uid, fields) => supabase?.from('user_profiles').update(fields).eq('user_id', uid),
  // Score fields (quiz_answers, criteria_scores, score, votes…)
  userScores:  (uid, fields) => supabase?.from('user_scores').update(fields).eq('user_id', uid),
  // Convenience: update event prices (array of {plan, price})
  eventPricing: (eventId, prices) =>
    supabase?.from('event_pricing').upsert(
      prices.map(p => ({ event_id: eventId, plan: p.plan, price: p.price })),
      { onConflict: 'event_id,plan' }
    ),
};

// ── RPC calls (PostGIS functions) ─────────────────────────────────────────
export const rpc = {
  discoverCandidates: (viewer_id, radius_km = 50, lim = 50) =>
    supabase?.rpc('discover_candidates', { viewer_id, radius_km, lim }),

  compatScore: (user_a, user_b) =>
    supabase?.rpc('compat_score', { user_a, user_b }),

  usersWithinRadius: (center_user_id, radius_km = 15) =>
    supabase?.rpc('users_within_radius', { center_user_id, radius_km }),

  searchUsers: (query, lim = 20) =>
    supabase?.rpc('search_users', { query, lim }),

  findCircleCandidates: (p_network_id, p_type, p_min_size = 4, p_max_size = 8) =>
    supabase?.rpc('find_circle_candidates', { p_network_id, p_type, p_min_size, p_max_size }),
};

// ── Auth shortcuts ────────────────────────────────────────────────────────
export const auth = supabase?.auth;

// ── Storage ───────────────────────────────────────────────────────────────
export const storage = {
  profiles: () => supabase?.storage.from('profiles'),
  events:   () => supabase?.storage.from('events'),
};

export default supabase;
